import { CSSProperties, useContext, useEffect, useRef, useState } from 'react';

import vtkGenericRenderWindow from 'vtk.js/Sources/Rendering/Misc/GenericRenderWindow';
import vtkOpenGLRenderWindow from 'vtk.js/Sources/Rendering/OpenGL/RenderWindow';
import vtkRenderer from 'vtk.js/Sources/Rendering/Core/Renderer';
import vtkCamera from 'vtk.js/Sources/Rendering/Core/Camera';

import vtkActor from 'vtk.js/Sources/Rendering/Core/Actor';
import vtkOrientationMarkerWidget from 'vtk.js/Sources/Interaction/Widgets/OrientationMarkerWidget';

import { colorValues } from '../shared/utils';
import { AppContext } from '../App';
import { ToolID } from '../shared/common';
import { DualViewPanel, getDualViewPanal, ViewOrientation } from '../shared/common';
import { Mesh } from '../model/Mesh';
import { vtkUtil, createTubeFilter } from '../shared/vtkUtils';
import { MarginLineModel } from '../model/MarginLineModel';

interface CanvasProps {
  id?: string;
  background?: string;
  panel?: DualViewPanel;
  syncCamera?: vtkCamera;
  hideOrientationMarker?: boolean;
}

interface RenderObject {
  genericRenderWindow: vtkGenericRenderWindow;
  renderWindow: vtkOpenGLRenderWindow;
  renderer: vtkRenderer;
}

const Canvas = ({ id, background, panel, syncCamera, hideOrientationMarker }: CanvasProps) => {
  const container = useRef(null);

  const {
    normalMeshes,
    buccalMeshes,
    light,
    color,
    zoomFit,
    setZoomFit,
    resetCamera,
    setResetCamera,
    refreshCanvas,
    setRefreshCanvas,
    viewOrientation,
    setViewOrientation,
    snapshot,
    setSnapshot,
    setSnapshotImage,
    dualView,
    marginLines,
    marginLineVisible,
    occlusalPlaneActor,
    occlusalPlaneVisible,
    meshPicker,
    setSelectedActor,
    currentTool,
    selectedMesh,
  } = useContext(AppContext);

  const [meshList, setMeshList] = useState([]);
  const [renderObject, setRenderObject] = useState<RenderObject>(null);
  const [orientMarkerWgt, setOrientMarkerWgt] = useState<vtkOrientationMarkerWidget>(null);
  const [syncCameraModified, setSyncCameraModified] = useState(false);
  const [marginLineList, setMarginLineList] = useState([]);

  useEffect(() => {
    if (container) {
      initCanvas();
    }
  }, [container]);

  useEffect(() => {
    if (!renderObject) {
      return;
    }
    if (!hideOrientationMarker) {
      const wgt = vtkUtil.createOrientationMarkerWidget(renderObject.renderWindow.getInteractor());
      setOrientMarkerWgt(wgt);
    }
    setViewOrientation(ViewOrientation.Front);
  }, [renderObject]);

  useEffect(() => {
    if (isAdjustment()) {
      setSelectedActorColor();
    }
  }, [selectedMesh]);

  useEffect(() => {
    if (normalMeshes) {
      if (isAdjustment()) {
        setSelectedActorColor();
      } else {
        normalMeshes.forEach((mesh) => {
          switchColor(mesh, color);
        });
      }
    }
    if (buccalMeshes) {
      buccalMeshes.forEach((mesh) => {
        switchColor(mesh, color);
      });
    }
    setRefreshCanvas(true);
  }, [color, currentTool]);

  useEffect(() => {
    if (normalMeshes) {
      normalMeshes.forEach((mesh) => {
        if (!(isAdjustment() && selectedMesh === mesh)) {
          switchLight(mesh, light);
        }
      });
    }
    if (buccalMeshes) {
      buccalMeshes.forEach((mesh) => {
        switchLight(mesh, light);
      });
    }
    setRefreshCanvas(true);
  }, [light]);

  useEffect(() => {
    if (zoomFit && renderObject) {
      renderObject.renderer.resetCamera();
      renderObject.renderWindow.render();
      setZoomFit(false);
    }
  }, [zoomFit]);

  useEffect(() => {
    if (resetCamera && renderObject) {
      setZoomFit(true);
      setResetCamera(false);
      setViewOrientation(ViewOrientation.Front);
    }
  }, [resetCamera]);

  useEffect(() => {
    if (!renderObject || viewOrientation === ViewOrientation.Unknown) {
      return;
    }

    const cam = renderObject.renderer.getActiveCamera();
    vtkUtil.setCameraOrientation(cam, viewOrientation);

    if (orientMarkerWgt) {
      orientMarkerWgt.updateMarkerOrientation();
    }
    renderObject.renderer.resetCamera();
    renderObject.renderWindow.render();
    setViewOrientation(ViewOrientation.Unknown);
  }, [viewOrientation]);

  useEffect(() => {
    if (normalMeshes && renderObject) {
      console.log('add normal mesh object to canvas', normalMeshes);
      meshPicker.setPickFromList(0);
      meshPicker.initializePickList();
      normalMeshes.forEach((mesh) => {
        if (!meshList.includes(mesh) && shouldAddMesh(mesh.name)) {
          addMesh(mesh);
        }
      });
      placeOcclusionPlane(renderObject.renderer);
    }
  }, [normalMeshes, renderObject]);

  useEffect(() => {
    if (buccalMeshes && renderObject) {
      console.log('add buccal mesh object to canvas');
      buccalMeshes.forEach((mesh) => {
        if (!meshList.includes(mesh) && shouldAddMesh(mesh.name)) {
          addMesh(mesh);
        }
      });
    }
  }, [buccalMeshes, renderObject]);

  // handle resize
  useEffect(() => {
    if (!container || !renderObject) {
      return;
    }
    const dom = container.current;
    const view = renderObject.renderWindow.getInteractor().getView();
    if (!dom || !view) {
      return;
    }
    const sz = view.getSize();
    if (sz[0] != dom.clientWidth || sz[1] != dom.clientHeight) {
      view.setSize(dom.clientWidth, dom.clientHeight);
      renderObject.renderWindow.render();
    }
  });

  // handle refresh
  useEffect(() => {
    window.addEventListener('refreshCanvas', renderCanvas);

    return () => {
      window.removeEventListener('refreshCanvas', renderCanvas);
    };
  });

  useEffect(() => {
    if (snapshot && renderObject) {
      handleSnapshot();
      setSnapshot(false);
    }
  }, [snapshot]);

  useEffect(() => {
    if (renderObject) {
      renderObject.renderWindow.render();
    }
    if (orientMarkerWgt) {
      orientMarkerWgt.updateMarkerOrientation();
    }
  }, [syncCameraModified]);

  // init margin lines
  useEffect(() => {
    if (renderObject) {
      marginLines.forEach((marginLine) => {
        if (shouldAddMesh(marginLine.meshName)) {
          const marginLineActor = createTubeFilter(marginLine.polydata);
          marginLineActor.getProperty().setColor(0, 1, 0);
          marginLineActor.getProperty().setAmbient(1.0);
          marginLineActor.setVisibility(marginLineVisible);

          const model: MarginLineModel = {
            name: marginLine.name,
            meshName: marginLine.meshName,
            arch: marginLine.arch,
            actor: marginLineActor,
          };
          marginLineList.push(model);
          renderObject.renderer.addActor(marginLineActor);
          renderObject.renderer.resetCamera();
          renderObject.renderWindow.render();
        }
      });
    }
  }, [marginLines, renderObject]);

  useEffect(() => {
    if (marginLineList.length > 0) {
      toggleMarginLineVisible(marginLineVisible);
    }
  }, [marginLineVisible]);

  useEffect(() => {
    if (refreshCanvas) {
      renderCanvas();
      setRefreshCanvas(false);
    }
  }, [refreshCanvas]);

  useEffect(() => {
    if (isAdjustment()) {
      enableMousePicker();
    }
  }, [currentTool]);

  // utilities start
  const initCanvas = () => {
    const bgArr = colorValues(background);
    const bgArrF = [bgArr[0] / 255.0, bgArr[1] / 255.0, bgArr[2] / 255.0];

    const genericRenderWindow = vtkGenericRenderWindow.newInstance({
      background: bgArrF,
    });

    genericRenderWindow.setContainer(container.current);

    const renderer = genericRenderWindow.getRenderer();
    const renderWindow = genericRenderWindow.getRenderWindow();
    if (syncCamera) {
      renderer.setActiveCamera(syncCamera);
      syncCamera.onModified(() => {
        setSyncCameraModified((v) => !v);
      });
    }

    renderer.resetCamera();
    renderer.updateLightsGeometryToFollowCamera();

    genericRenderWindow.resize();
    setRenderObject({ genericRenderWindow, renderWindow, renderer });
  };

  const setSelectedActorColor = () => {
    if (normalMeshes) {
      normalMeshes.forEach((mesh) => {
        const isSelected = selectedMesh === mesh;
        switchColor(mesh, isSelected ? false : color);
        if (isSelected) {
          mesh.actor.getProperty().setColor([54 / 255.0, 176 / 255.0, 201 / 255.0]);
        }
      });
    }
  };

  const enableMousePicker = () => {
    if (!renderObject) {
      return;
    }

    renderObject.renderWindow.getInteractor().onLeftButtonRelease((callData) => {
      if (renderObject.renderer !== callData.pokedRenderer) {
        return;
      }

      const pos = callData.position;
      //meshPicker.setTolerance(0.1)
      meshPicker.pick([pos.x, pos.y, 0.0], renderObject.renderer);

      const actors = meshPicker.getActors();
      let mesh_actors = actors.filter((actor: vtkActor) => {
        return actor !== occlusalPlaneActor;
      });

      let selected_actor = null;

      if (mesh_actors.length > 0) {
        selected_actor = mesh_actors[0];
      }

      setSelectedActor(selected_actor);
    });
  };

  const shouldAddMesh = (meshName: string) => {
    if (!dualView) {
      return true;
    }

    return getDualViewPanal(meshName) == panel;
  };

  const addMesh = (obj: Mesh) => {
    console.log('addMesh', obj);
    setMeshList((oldarray) => [...oldarray, obj]);
    if (obj.actor) {
      console.log('addActor', obj.actor);
      obj.actor.setVisibility(obj.visible);
      switchLight(obj, light);
      if (isAdjustment()) {
        let isSelected = selectedMesh === obj;
        switchColor(obj, isSelected ? false : color);
        if (isSelected) {
          obj.actor.getProperty().setColor([54 / 255.0, 176 / 255.0, 201 / 255.0]);
        }
      } else {
        switchColor(obj, color);
      }
      renderObject.renderer.addActor(obj.actor);
      renderObject.renderer.resetCamera();
      renderObject.renderWindow.render();
      if (obj.isCommonScanMesh()) {
        meshPicker.addPickList(obj.actor);
      }
    }
  };

  const switchColor = (mesh: Mesh, on: boolean) => {
    if (!mesh || !mesh.actor) {
      return;
    }

    if (on) {
      if (mesh.isTextured()) {
        mesh.revertTexture();
      }
    } else {
      if (mesh.isTextured()) {
        mesh.removeTexture();
      }
    }

    if (mesh.isTextured()) {
      switchLight(mesh, on ? light : true);
    } else {
      switchLight(mesh, true);
    }

    const scalarOn = on && mesh.isTrueColorSupported();
    mesh.actor.getMapper().setScalarVisibility(scalarOn);
    const clr = scalarOn ? { r: 1, g: 1, b: 1 } : mesh.preferColor;
    mesh.actor.getProperty().setColor(clr.r, clr.g, clr.b);

    console.log('set color to', color, 'for mesh', mesh);
    if (renderObject) {
      renderObject.renderWindow.render();
    }
  };

  const switchLight = (mesh: Mesh, on: boolean) => {
    if (mesh && mesh.actor) {
      mesh.actor.getProperty().setAmbient(on ? 0.0 : 1.0);
      mesh.actor.getProperty().setDiffuse(on ? 1.0 : 0.0);
      console.log('set light to', on, 'for mesh', mesh);
    }
    if (renderObject) {
      renderObject.renderWindow.render();
    }
  };

  const renderCanvas = () => {
    if (renderObject) {
      renderObject.renderWindow.render();
    }
  };

  const handleSnapshot = () => {
    const view = renderObject.renderWindow.getInteractor().getView();
    if (view) {
      view.captureNextImage().then((imgSrc) => {
        setSnapshotImage(imgSrc);
      });
      renderObject.renderWindow.render();
    }
  };

  const placeOcclusionPlane = (renderer: vtkRenderer) => {
    vtkUtil.placeOcclusionPlane(renderer, occlusalPlaneActor);
    occlusalPlaneActor.setVisibility(occlusalPlaneVisible && isAdjustment());
  };

  const toggleMarginLineVisible = (visible: boolean) => {
    marginLineList.forEach((marginLine) => {
      if (marginLine.actor) {
        const visible = Boolean(meshList.find((mesh) => mesh.name == marginLine.meshName)?.actor?.getVisibility());
        marginLine.actor.setVisibility(marginLineVisible && visible);
      }
    });
    renderCanvas();
  };

  const isAdjustment = () => {
    return ToolID.OrientationAdjustment === currentTool;
  };
  // utilities end

  const style: CSSProperties = { width: '100%', height: '100%' };
  return <div id={id} ref={container} style={style}></div>;
};

export default Canvas;
